import jwtDecode from "jwt-decode";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AxiosProvider } from "../../connections/config";
import Loader from "../Loader";
import Modal from "../Modal";
import "./style.scss";

export default function PointsTransferModal({
  open,
  setOpen,
  cashbackValue,
  userToken,
  userId,
  userCpf,
  onTransferSuccess,
  colors,
  initialPrograms = [],
}) {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [pointBalance, setPointBalance] = useState(0);
  const [conversionRate, setConversionRate] = useState(0);
  const [pointsToReceive, setPointsToReceive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transferAmount, setTransferAmount] = useState("");

  useEffect(() => {
    if (open) {
      // Se já temos os programas, usa eles
      if (initialPrograms.length > 0) {
        setPrograms(initialPrograms);
        if (initialPrograms.length === 1) {
          setSelectedProgram(initialPrograms[0]);
        }
      } else {
        // Só faz a requisição se não temos programas
        fetchUserPrograms();
      }
    }
  }, [open, initialPrograms]);

  useEffect(() => {
    if (selectedProgram) {
      fetchPointBalance();
    }
  }, [selectedProgram]);

  useEffect(() => {
    if (transferAmount && selectedProgram) {
      const amount = parseFloat(transferAmount);
      if (!isNaN(amount) && amount > 0) {
        const rate = parseFloat(
          selectedProgram.program.valuePointsInRealCashback
        );
        const points = amount / rate;
        // Arredonda para 2 casas decimais
        setPointsToReceive(Math.round(points * 100) / 100);
      } else {
        setPointsToReceive(0);
      }
    } else {
      setPointsToReceive(0);
    }
  }, [transferAmount, selectedProgram]);

  const fetchUserPrograms = async () => {
    setLoading(true);
    try {
      const decodedToken = jwtDecode(userToken);
      const apiKey = decodedToken?.selectedCompany?.code || "";

      const response = await AxiosProvider.get(
        "https://api-points.clubecerto.com.br/point-programs/programs-by-user",
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "x-api-key": apiKey,
            local: "web",
          },
        }
      );

      if (response.data.count > 0) {
        setPrograms(response.data.rows);
        if (response.data.rows.length === 1) {
          setSelectedProgram(response.data.rows[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching programs:", error);
      toast.error("Erro ao buscar programas de pontos");
    } finally {
      setLoading(false);
    }
  };

  const fetchPointBalance = async () => {
    if (!selectedProgram || !userCpf) return;

    setLoading(true);
    try {
      const decodedToken = jwtDecode(userToken);
      const apiKey = decodedToken?.selectedCompany?.code || "";

      const response = await AxiosProvider.get(
        `https://api-points.clubecerto.com.br/point-programs/${selectedProgram.idProgram}/users/extract/${userCpf}`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "x-api-key": apiKey,
            local: "web",
          },
        }
      );

      setPointBalance(parseFloat(response.data.user.currentBalance || 0));
      setConversionRate(
        parseFloat(selectedProgram.program.valuePointsInRealCashback)
      );
    } catch (error) {
      console.error("Error fetching point balance:", error);
      toast.error("Erro ao buscar saldo de pontos");
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    const amount = parseFloat(transferAmount);

    if (!selectedProgram) {
      toast.error("Selecione um programa de pontos");
      return;
    }

    if (isNaN(amount) || amount <= 0) {
      toast.error("Digite um valor válido para transferir");
      return;
    }

    if (amount > cashbackValue) {
      toast.error("Valor maior que o saldo disponível");
      return;
    }

    setLoading(true);
    try {
      const decodedToken = jwtDecode(userToken);
      const apiKey = decodedToken?.selectedCompany?.code || "";

      await AxiosProvider.post(
        "https://integrations.clubecerto.com.br/cashback-to-points/convert",
        // "http://localhost:3030/cashback-to-points/convert",
        {
          cpf: userCpf,
          points: parseFloat(pointsToReceive.toFixed(2)),
          cashbackValue: amount,
          idProgram: selectedProgram.idProgram,
        },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "x-api-key": apiKey,
            local: "web",
          },
        }
      );

      toast.success(
        <>
          Transferência realizada com sucesso!
          <br />
          Você recebeu <b>{pointsToReceive.toFixed(2)} pontos</b>
        </>
      );

      if (onTransferSuccess) {
        onTransferSuccess();
      }

      setOpen(false);
      setTransferAmount("");
      setSelectedProgram(null);
    } catch (error) {
      console.error("Error during transfer:", error);
      toast.error("Erro ao realizar transferência");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <div className="pointsTransferModal">
        <div className="modalHeader">
          <h2>Transferir para Pontos</h2>
          <button className="closeButton" onClick={() => setOpen(false)}>
            ×
          </button>
        </div>

        <div className="modalContent">
          <div className="balanceCard">
            <div className="balanceLabel">Saldo disponível em Cashback</div>
            <div className="balanceValue">
              R$ {cashbackValue.toFixed(2).replace(".", ",")}
            </div>
          </div>

          {programs.length > 0 ? (
            <>
              <div className="formGroup">
                <label className="formLabel">
                  Selecione o programa de pontos
                </label>
                <div className="selectWrapper">
                  <select
                    className="formSelect"
                    value={selectedProgram?.idProgram || ""}
                    onChange={(e) => {
                      const program = programs.find(
                        (p) => p.idProgram === parseInt(e.target.value)
                      );
                      setSelectedProgram(program);
                    }}
                  >
                    <option value="">Escolha um programa</option>
                    {programs.map((program) => (
                      <option key={program.idProgram} value={program.idProgram}>
                        {program.program.programName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedProgram && (
                <>
                  <div className="infoCards">
                    <div className="infoCard">
                      <div className="infoLabel">Saldo atual de pontos</div>
                      <div className="infoValue">
                        {pointBalance.toFixed(2)} pts
                      </div>
                    </div>
                    <div className="infoCard">
                      <div className="infoLabel">Taxa de conversão</div>
                      <div className="infoValue">
                        R$ {conversionRate.toFixed(2)} = 1 ponto
                      </div>
                    </div>
                  </div>

                  <div className="formGroup">
                    <label className="formLabel">Valor a transferir</label>
                    <div className="inputWrapper">
                      <span className="inputPrefix">R$</span>
                      <input
                        className="formInput"
                        type="number"
                        value={transferAmount}
                        onChange={(e) => setTransferAmount(e.target.value)}
                        placeholder="0,00"
                        step="0.01"
                        min="0"
                        max={cashbackValue}
                      />
                    </div>
                  </div>

                  {transferAmount && parseFloat(transferAmount) > 0 && (
                    <div className="conversionPreview">
                      <div className="conversionArrow">↓</div>
                      <div className="conversionResult">
                        <span className="resultLabel">Você receberá</span>
                        <span className="resultValue">
                          {pointsToReceive.toFixed(2)} pontos
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="emptyState">
              <p>Nenhum programa de pontos encontrado.</p>
            </div>
          )}
        </div>

        {selectedProgram && (
          <div className="modalFooter">
            <button
              className="btn btnCancel"
              onClick={() => {
                setOpen(false);
                setTransferAmount("");
                setSelectedProgram(null);
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btnConfirm"
              onClick={handleTransfer}
              disabled={
                !transferAmount || parseFloat(transferAmount) <= 0 || loading
              }
              style={{
                backgroundColor: colors?.backgroundColor || "#00A24D",
              }}
            >
              {loading ? "Processando..." : "Confirmar Transferência"}
            </button>
          </div>
        )}
      </div>
      {loading && <Loader />}
    </Modal>
  );
}
